/**
 * One URL per search name (thiruvananthapuram AND trivandrum, etc.).
 * Content stays locally unique — not copy-paste twins.
 */
export const serviceLocations = [
  {
    slug: 'ernakulam',
    name: 'Ernakulam',
    aliases: ['Ernakulam'],
    twinSlug: 'kochi',
    twinLabel: 'Kochi',
    priority: 'P0',
    blurb:
      'Homvisit provides corporate health camps and onsite phlebotomy across Ernakulam district — from Aluva and North Paravur to Trippunithura — for offices, institutions, and diagnostic partners who search specifically for Ernakulam coverage.',
    cities: ['Aluva', 'North Paravur', 'Trippunithura', 'Kalamassery', 'Angamaly', 'Perumbavoor', 'Muvattupuzha'],
    responseNote:
      'Ernakulam district sites outside the Kochi core often need 5–10 days of lead time so we can stage collectors and gate passes.',
    localExperience:
      'Ernakulam buyers include industrial estates, educational campuses, and district offices that want camps closer to employees than a central Kochi hotel venue.',
    ecosystem:
      'Many samples still route through Kochi lab networks; we plan handoff timing from eastern and northern Ernakulam towns accordingly.',
    faqs: [
      {
        q: 'Is Ernakulam the same page as Kochi?',
        a: 'No — Kochi has its own URL for city searches. Ernakulam covers the wider district footprint and institutional buyers using the official district name.',
      },
      {
        q: 'Can you run a camp in Aluva or Angamaly?',
        a: 'Yes. Share headcount and shift windows so we staff travel time correctly.',
      },
    ],
    testimonial:
      'District-name buyers care about reach beyond MG Road — we staff for that map.',
  },
  {
    slug: 'kochi',
    name: 'Kochi',
    aliases: ['Kochi'],
    twinSlug: 'ernakulam',
    twinLabel: 'Ernakulam',
    priority: 'P0',
    blurb:
      'Kochi is Kerala’s densest corporate corridor. Homvisit runs onsite phlebotomy and corporate medical camps for IT parks, offices, hospitals, and diagnostic partners searching for Kochi specifically.',
    cities: [
      'Kakkanad / Infopark',
      'Edappally',
      'Vyttila',
      'Fort Kochi / Mattancherry',
      'Palarivattom',
      'Kadavanthra',
      'Marine Drive belt',
    ],
    responseNote:
      'Metro Kochi camps are often schedulable within 3–7 days; multi-tower Infopark campuses need longer queue planning.',
    localExperience:
      'Frequent demand from corporate offices, media campuses, and diagnostic partners needing overflow collectors for Kochi home routes and employee camps.',
    ecosystem:
      'Strong private hospital and diagnostic presence makes Kochi ideal for white-label and partner collection models.',
    faqs: [
      {
        q: 'Do you cover Infopark / Kakkanad?',
        a: 'Yes — we regularly plan phlebotomy stations for campus-style corporate health camps in the Kakkanad belt.',
      },
      {
        q: 'Same-week Kochi camps?',
        a: 'Often yes for modest headcounts; large multi-shift sites should book earlier.',
      },
    ],
    testimonial:
      'Kochi buyers need speed and queue discipline — we staff for throughput.',
  },
  {
    slug: 'thiruvananthapuram',
    name: 'Thiruvananthapuram',
    aliases: ['Thiruvananthapuram'],
    twinSlug: 'trivandrum',
    twinLabel: 'Trivandrum',
    priority: 'P0',
    blurb:
      'Official-name searches for Thiruvananthapuram usually come from government, PSU, and institutional buyers. Homvisit deploys phlebotomist teams for capital-region corporate and workplace health camps under that formal district identity.',
    cities: [
      'Secretariat / central offices belt',
      'Pattom',
      'Kesavadasapuram',
      'Ulloor',
      'Neyyattinkara',
      'Nedumangad',
      'Attingal',
    ],
    responseNote:
      'Institutional Thiruvananthapuram camps often need extra lead time for security clearance and emp-code lists — plan 1–2 weeks when possible.',
    localExperience:
      'PSU and government screening days emphasise ID discipline, bilingual communication, and calm queues more than flashy camp branding.',
    ecosystem:
      'Proximity to medical colleges and tertiary hospitals means partners often want labelling compatible with institutional lab intake rules.',
    faqs: [
      {
        q: 'Why is there also a Trivandrum page?',
        a: 'People search both names. This page targets Thiruvananthapuram; Trivandrum has its own URL with city/tech-park emphasis.',
      },
      {
        q: 'Malayalam briefings?',
        a: 'Yes — field teams commonly operate in Malayalam and English.',
      },
    ],
    testimonial:
      'Formal capital buyers reward paperwork accuracy — we brief collectors for that.',
  },
  {
    slug: 'trivandrum',
    name: 'Trivandrum',
    aliases: ['Trivandrum'],
    twinSlug: 'thiruvananthapuram',
    twinLabel: 'Thiruvananthapuram',
    priority: 'P0',
    blurb:
      'Trivandrum is the everyday search name for Kerala’s capital city. Homvisit runs corporate health camps and onsite phlebotomy for Technopark, private offices, and institutions that type “Trivandrum” when they look for a collection partner.',
    cities: [
      'Technopark corridor',
      'Kazhakuttam',
      'Kulathoor',
      'Sreekaryam',
      'Pattom',
      'East Fort / city centre',
      'Kowdiar',
    ],
    responseNote:
      'Technopark and central Trivandrum sites: commonly about one week lead time; multi-building campus camps need earlier slot design.',
    localExperience:
      'Private corporate checkups and tech-park wellness days dominate Trivandrum search intent — faster scheduling language than formal government RFPs.',
    ecosystem:
      'Many private diagnostic partners hub in the city; we align camp end-times with their pickup windows.',
    faqs: [
      {
        q: 'Is Trivandrum different from Thiruvananthapuram operationally?',
        a: 'Same capital region. Separate URLs match how people search; we deploy one Homvisit team either way.',
      },
      {
        q: 'Technopark camps?',
        a: 'Yes — station count is planned around building access rules and employee shifts.',
      },
    ],
    testimonial:
      'Trivandrum tech-park camps reward punctual start times and clear emp-ID flow.',
  },
  {
    slug: 'kozhikode',
    name: 'Kozhikode',
    aliases: ['Kozhikode'],
    twinSlug: 'calicut',
    twinLabel: 'Calicut',
    priority: 'P0',
    blurb:
      'Kozhikode district coverage for North Kerala corporate and institutional camps — Homvisit supplies onsite phlebotomy teams for buyers who use the official Kozhikode name.',
    cities: ['Kozhikode city', 'Medical college belt', 'Feroke', 'Ramanattukara', 'Koyilandy', 'Balussery'],
    responseNote:
      'District camps outside the city core need route planning; combine with home-collection overflow only when timelines allow.',
    localExperience:
      'Official Kozhikode naming shows up in institutional POs and district-level wellness programs.',
    ecosystem:
      'Growing private diagnostics across Malabar — strong fit for collection partnerships staged from Kozhikode.',
    faqs: [
      {
        q: 'Do you also rank for Calicut?',
        a: 'Yes — Calicut has a dedicated page for English city searches. Operations cover the same metro.',
      },
    ],
    testimonial:
      'Kozhikode district work needs honest travel ETAs between towns.',
  },
  {
    slug: 'calicut',
    name: 'Calicut',
    aliases: ['Calicut'],
    twinSlug: 'kozhikode',
    twinLabel: 'Kozhikode',
    priority: 'P0',
    blurb:
      'Calicut is still how many HR teams and lab partners search for North Kerala’s commercial hub. Homvisit runs corporate camps and mobile phlebotomy for Calicut city buyers.',
    cities: [
      'Mavoor Road belt',
      'Focus Mall area',
      'Panniyankara',
      'Eranhipalam',
      'Feroke',
      'Ramanattukara',
    ],
    responseNote: 'City Calicut camps often within a week; suburban clusters need same-day route design.',
    localExperience:
      'Calicut searchers often want employee camps plus diagnostic brand expansion support in Malabar.',
    ecosystem:
      'City lab networks make Calicut a practical hub for partner white-label collection.',
    faqs: [
      {
        q: 'Is Calicut the same as Kozhikode?',
        a: 'Same metro region — separate URLs for search names. One Homvisit operations plan.',
      },
    ],
    testimonial:
      'Calicut camp buyers care about early slots and clear Malayalam communication.',
  },
  {
    slug: 'thrissur',
    name: 'Thrissur',
    aliases: ['Thrissur'],
    priority: 'P1',
    blurb:
      'Workplace health camps and phlebotomy staffing for Thrissur’s commercial, cultural, and institutional employers.',
    cities: ['Thrissur city', 'Guruvayur road belt', 'Ollur', 'Kunnamkulam', 'Chalakudy (border planning)'],
    responseNote: 'City centre camps: ~1 week typical; industrial outskirts need travel-inclusive scheduling.',
    localExperience:
      'Thrissur sees steady mid-size employer camps — jewellery, trading, education, and healthcare admin offices.',
    ecosystem: 'Well-connected to Ernakulam for partner lab logistics on many packages.',
    faqs: [
      {
        q: 'Thrissur + Chalakudy same day?',
        a: 'Sometimes for small headcounts; usually separate slots to protect sample timelines.',
      },
    ],
    testimonial: 'Mid-size Thrissur camps reward flexible station setups in compact offices.',
  },
  {
    slug: 'kollam',
    name: 'Kollam',
    aliases: ['Kollam'],
    priority: 'P1',
    blurb:
      'Corporate and institutional sample collection camps in Kollam with Homvisit phlebotomy teams.',
    cities: ['Kollam city', 'Chinnakada belt', 'Kottiyam', 'Karunagappally', 'Punalur (on request)'],
    responseNote:
      'Coastal city camps are straightforward; Punalur/eastward sites need earlier confirmation for travel.',
    localExperience:
      'Kollam demand often links to manufacturing, ports-adjacent employers, and education campuses.',
    ecosystem: 'Many packages route toward Trivandrum or Kochi lab networks depending on the partner.',
    faqs: [
      {
        q: 'Karunagappally coverage?',
        a: 'Yes for planned camps; share headcount for travel staffing.',
      },
    ],
    testimonial: 'Kollam works well on southern multi-day rollouts with Trivandrum.',
  },
  {
    slug: 'malappuram',
    name: 'Malappuram',
    aliases: ['Malappuram'],
    priority: 'P1',
    blurb:
      'Phlebotomist teams for corporate and community-linked health camps across Malappuram’s spread-out towns.',
    cities: ['Malappuram', 'Manjeri', 'Perinthalmanna', 'Tirur', 'Kottakkal', 'Nilambur (on request)'],
    responseNote:
      'Town-to-town distance matters — prefer single-town camps per day unless headcounts are small.',
    localExperience:
      'High population with distributed commercial centres; Malayalam-first communication is essential.',
    ecosystem: 'Often combined with Kozhikode / Calicut logistics hubs.',
    faqs: [
      {
        q: 'Perinthalmanna and Tirur same morning?',
        a: 'Usually better as separate sessions.',
      },
    ],
    testimonial: 'Malappuram success is routing discipline more than raw headcount.',
  },
  {
    slug: 'kannur',
    name: 'Kannur',
    aliases: ['Kannur'],
    priority: 'P1',
    blurb:
      'Onsite phlebotomy and workplace camps for Kannur organisations expanding preventive screening.',
    cities: ['Kannur city', 'Thalassery', 'Payyanur', 'Mattannur', 'Iritty (on request)'],
    responseNote: 'Coastal belt camps weekly-plannable; eastern towns need travel buffers.',
    localExperience:
      'Kannur + Thalassery employers often request compact camps with strong local language support.',
    ecosystem: 'North Malabar coverage pairs with Kozhikode/Calicut patterns.',
    faqs: [
      {
        q: 'Payyanur camps?',
        a: 'Yes for scheduled camps and partner projects.',
      },
    ],
    testimonial: 'North Kerala coverage is a network game — Join Network keeps collectors warm.',
  },
  {
    slug: 'palakkad',
    name: 'Palakkad',
    aliases: ['Palakkad'],
    priority: 'P1',
    blurb:
      'Industrial and workplace health checkup camps in Palakkad — factories, estates-adjacent employers, and offices.',
    cities: ['Palakkad town', 'Kanjikode industrial belt', 'Ottapalam', 'Chittur', 'Mannarkkad (on request)'],
    responseNote:
      'Kanjikode industrial camps often need early-morning slots aligned to shifts.',
    localExperience:
      'Factories Act / occupational health style screening days are a common Palakkad pattern.',
    ecosystem: 'Industrial belt logistics may require PPE and safety induction.',
    faqs: [
      {
        q: 'Industrial estate access?',
        a: 'Yes, with prior gate passes and safety briefings from the employer.',
      },
    ],
    testimonial: 'Palakkad rewards collectors comfortable with factory timelines.',
  },
  {
    slug: 'alappuzha',
    name: 'Alappuzha',
    aliases: ['Alappuzha'],
    twinSlug: 'alleppey',
    twinLabel: 'Alleppey',
    priority: 'P2',
    blurb:
      'Corporate and institutional collection camps in Alappuzha district — Homvisit for buyers using the official Malayalam/English administrative name.',
    cities: ['Alappuzha town', 'Cherthala', 'Kayamkulam', 'Haripad', 'Chengannur (border planning)'],
    responseNote: 'Town camps are efficient; backwater-adjacent sites need realistic travel ETAs.',
    localExperience:
      'Tourism-linked employers and local institutions request periodic screening more than daily home routes.',
    ecosystem: 'Often paired with Ernakulam or Kollam lab partners.',
    faqs: [
      {
        q: 'Looking for Alleppey?',
        a: 'Use /services/alleppey — same region, separate search URL.',
      },
    ],
    testimonial: 'Alappuzha camps are usually compact hall or office setups.',
  },
  {
    slug: 'alleppey',
    name: 'Alleppey',
    aliases: ['Alleppey'],
    twinSlug: 'alappuzha',
    twinLabel: 'Alappuzha',
    priority: 'P2',
    blurb:
      'Alleppey remains a common tourism and English search name. Homvisit runs planned corporate and institutional phlebotomy camps for buyers who search Alleppey rather than Alappuzha.',
    cities: ['Alleppey town', 'Beach / tourism belt offices', 'Cherthala', 'Haripad'],
    responseNote: 'Tourism-season calendars can affect venue availability — lock dates early.',
    localExperience:
      'Hospitality and local commerce employers often discover Homvisit via the Alleppey spelling.',
    ecosystem: 'Same operational district as Alappuzha with English-name landing intent.',
    faqs: [
      {
        q: 'Alleppey vs Alappuzha?',
        a: 'Same district region. Two URLs match two search habits.',
      },
    ],
    testimonial: 'Alleppey searchers want clear English pages and simple camp booking.',
  },
  {
    slug: 'kottayam',
    name: 'Kottayam',
    aliases: ['Kottayam'],
    priority: 'P2',
    blurb:
      'Education-heavy and institutional health camps in Kottayam with Homvisit phlebotomy support.',
    cities: ['Kottayam town', 'Gandhinagar belt', 'Changanassery', 'Pala', 'Ettumanoor'],
    responseNote: 'Campus camps need student/staff ID workflows agreed in advance.',
    localExperience:
      'Colleges, publishing, and healthcare admin offices drive much of the scheduled demand.',
    ecosystem: 'Central Travancore positioning links well to Ernakulam diagnostics.',
    faqs: [
      {
        q: 'Campus faculty camps?',
        a: 'Yes — queue flow adapts to academic calendars and hall availability.',
      },
    ],
    testimonial: 'Kottayam buyers value orderly camps over aggressive throughput.',
  },
  {
    slug: 'pathanamthitta',
    name: 'Pathanamthitta',
    aliases: ['Pathanamthitta'],
    priority: 'P2',
    blurb:
      'Local corporate and institutional camps in Pathanamthitta — planned coverage rather than high-frequency metro routes.',
    cities: ['Pathanamthitta town', 'Thiruvalla', 'Adoor', 'Ranni', 'Pandalam'],
    responseNote:
      'Thiruvalla/Adoor are easier hubs; interior towns are scheduled with deliberate travel time.',
    localExperience:
      'Lower volume than Kochi — reliability on the day matters more than constant availability claims.',
    ecosystem: 'Often supports partners whose main lab sits in Trivandrum or Kochi.',
    faqs: [
      {
        q: 'Multi-town same day?',
        a: 'Rarely for large headcounts. Prefer focused single-town camps.',
      },
    ],
    testimonial: 'Pathanamthitta is a planning district — we don’t pretend it’s metro density.',
  },
  {
    slug: 'idukki',
    name: 'Idukki',
    aliases: ['Idukki'],
    priority: 'P2',
    blurb:
      'Estate, plantation, and organisational health camps in Idukki with travel-aware phlebotomy staffing.',
    cities: ['Thodupuzha', 'Kattappana', 'Munnar belt (planned)', 'Adimali', 'Nedumkandam (on request)'],
    responseNote:
      'Hill terrain means longer lead times and weather-aware scheduling — not instant metro SLAs.',
    localExperience:
      'Plantation and estate medical days need rugged logistics and clear sample hold limits.',
    ecosystem: 'Partners should pre-agree transport expectations before promising exotic panels.',
    faqs: [
      {
        q: 'Next-day Munnar?',
        a: 'No — hill deployments are planned. Share dates early.',
      },
    ],
    testimonial: 'Idukki success = honest ETAs + cold-chain realism.',
  },
  {
    slug: 'wayanad',
    name: 'Wayanad',
    aliases: ['Wayanad'],
    priority: 'P2',
    blurb:
      'Workplace and estate-linked screening camps in Wayanad with Homvisit collection teams on planned deployments.',
    cities: ['Kalpetta', 'Sulthan Bathery', 'Mananthavady', 'Meenangadi'],
    responseNote: 'Plan as expedition-style camp days; avoid stacking with heavy Kozhikode same-day loads.',
    localExperience:
      'Tourism, estates, and local institutions drive intermittent but meaningful camp demand.',
    ecosystem: 'Usually staged via Kozhikode / Calicut partner logistics.',
    faqs: [
      {
        q: 'Full-time Wayanad staff?',
        a: 'Coverage is network + planned deployment based on confirmed volume.',
      },
    ],
    testimonial: 'Wayanad is quality-over-velocity territory.',
  },
  {
    slug: 'kasaragod',
    name: 'Kasaragod',
    aliases: ['Kasaragod'],
    priority: 'P2',
    blurb:
      'Northern-most Kerala district coverage for planned corporate and partner collection camps in Kasaragod.',
    cities: ['Kasaragod town', 'Kanhangad', 'Nileshwar', 'Uppala (on request)'],
    responseNote:
      'Distance from central Kerala hubs means batching camps and confirming logistics early.',
    localExperience:
      'Completes Homvisit’s statewide map — important for partners promising Kerala-wide coverage.',
    ecosystem: 'Often coordinated with Kannur / Kozhikode operating patterns.',
    faqs: [
      {
        q: 'Weekly roster?',
        a: 'On-demand/planned rather than high-frequency metro coverage.',
      },
    ],
    testimonial:
      'Statewide credibility includes showing up in Kasaragod when partners need it — with real planning.',
  },
]

/** Old combined slugs → primary new slug */
export const locationRedirects = {
  'ernakulam-kochi': 'kochi',
  'thiruvananthapuram-trivandrum': 'trivandrum',
  'kozhikode-calicut': 'calicut',
}

export function getServiceLocation(slug) {
  return serviceLocations.find((l) => l.slug === slug) || null
}

export function primaryKeywordsFor(location) {
  const names = location.aliases
  const phrases = []
  names.forEach((loc) => {
    phrases.push(
      `corporate health camp ${loc}`,
      `corporate medical camp ${loc}`,
      `onsite phlebotomy ${loc}`,
      `phlebotomy services ${loc}`,
      `employee health checkup camp ${loc}`,
      `sample collection ${loc}`
    )
  })
  return phrases
}

export const DISTRICT_OPTIONS = [
  'Thiruvananthapuram',
  'Kollam',
  'Pathanamthitta',
  'Alappuzha',
  'Kottayam',
  'Idukki',
  'Ernakulam',
  'Thrissur',
  'Palakkad',
  'Malappuram',
  'Kozhikode',
  'Wayanad',
  'Kannur',
  'Kasaragod',
  'Other / Willing to travel',
]
